/**
 * VRG-718 — which slice of Accounts → Companies a company falls in, and the type badge it wears.
 *
 * ## Why a segment, and why exactly one per company
 *
 * Accounts → Companies used to load everything (`includeDemo: true`): real tenants, acquisition
 * demos, Vera's own internal row, test companies and example companies (the VRG-280 canary,
 * Maison Vera). Maison Vera, `is_example = true` with `kind = 'standard'`, read as "Active", so
 * the list's headline number and its first screen were not "the companies that signed up".
 *
 * A SEGMENT is a partition: every company is in exactly one, so the six chips add up to the whole
 * table and selecting all six is the same as no filter. Precedence, first match wins:
 *
 *   archived   `archived = true`, whatever else it is. Archiving is the strongest statement a
 *              staffer makes about a row ("out of the way"), so it outranks what kind it is.
 *   internal   `kind = 'internal'` (Vera itself, exactly one row).
 *   demo       `kind = 'demo'` (an acquisition demo; also carries `is_example`).
 *   test       `is_test = true`.
 *   example    `is_example = true` (the canary, Maison Vera, any other sample tenant).
 *   signed_up  everything else: `kind = 'standard'`, not test, not example, not archived.
 *
 * The TYPE BADGE is the same ladder WITHOUT the archived rung, and null for a signed-up company:
 * an archived demo still reads "Demo" in its row while it sits under the Archived chip, because
 * the badge says what a company IS and the segment says where the list files it.
 *
 * ## One rule, two implementations, one guard
 *
 * The API computes the segment in SQL (`companySegmentSql` in
 * `apps/api/src/infrastructure/repositories/company-list-segments.ts`) so the filter, the total
 * and the value returned on each row come from ONE expression and cannot disagree. This function
 * is the readable statement of the same rule; `company-segments.db.guard.spec.ts` evaluates the
 * SQL over every flag combination on a real Postgres and requires it to equal this.
 *
 * Suspended is not a segment: a suspended company is still a signed-up company, and the Status
 * column says it is suspended.
 */

export const COMPANY_SEGMENTS = ['signed_up', 'demo', 'example', 'test', 'internal', 'archived'] as const
export type CompanySegment = (typeof COMPANY_SEGMENTS)[number]

/**
 * What Accounts → Companies shows before anyone touches a filter: ALL SIX — every company.
 *
 * This REVERSES VRG-718's original `['signed_up']` default (2026-11-02, user directive: the
 * default view "should not filter to just signup, but show all"). VRG-718's argument was that
 * the headline total should read "the companies that signed up" rather than counting demos,
 * examples, test rows and Vera's own internal row. That argument is right about the NUMBER and
 * was overruled on the VIEW: an internal operator opening Companies wants the whole table, and a
 * default that hides rows makes a company they are looking for appear not to exist — which is
 * the more expensive failure, because it is silent.
 *
 * KNOWN COST, stated rather than hidden: the header total now counts demos, examples, test and
 * internal rows. Narrowing to Signed up is one chip away, and because the six segments partition
 * the table, "all six" is exactly "no filter".
 *
 * The seven company PICKERS are untouched: they send no `segments` at all and keep the
 * repository's own exclusive default. That is a separate decision — see EncodeTagsModal, which
 * opts back in to demos deliberately via `includeDemo`.
 */
export const DEFAULT_COMPANY_SEGMENTS: readonly CompanySegment[] = [...COMPANY_SEGMENTS]

export const COMPANY_SEGMENT_LABELS: Record<CompanySegment, string> = {
  signed_up: 'Signed up',
  demo: 'Demo',
  example: 'Example',
  test: 'Test',
  internal: 'Internal',
  archived: 'Archived',
}

/** The row badge. A signed-up company wears none. */
export type CompanyType = 'demo' | 'example' | 'test' | 'internal'

export interface CompanySegmentFlags {
  archived: boolean
  kind: 'standard' | 'demo' | 'internal' | string
  is_test: boolean
  is_example: boolean
}

export const companyTypeOf = (c: Omit<CompanySegmentFlags, 'archived'>): CompanyType | null => {
  if (c.kind === 'internal') return 'internal'
  if (c.kind === 'demo') return 'demo'
  if (c.is_test) return 'test'
  if (c.is_example) return 'example'
  return null
}

export const companySegmentOf = (c: CompanySegmentFlags): CompanySegment =>
  c.archived ? 'archived' : companyTypeOf(c) ?? 'signed_up'

export const isCompanySegment = (v: unknown): v is CompanySegment =>
  typeof v === 'string' && (COMPANY_SEGMENTS as readonly string[]).includes(v)
